import { GridSortItem, GridSortModel } from '@mui/x-data-grid-premium'
import { first } from 'lodash'
import { Plus, Trash2 } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { Button } from '~/@/components/ui/button'
import { cn } from '~/@/lib/utils'
import { useGlobalContext } from '~/common/hooks/useGlobalContext'
import { useTypedTranslation } from '~/common/hooks/useTypedTranslation'
import { LeftNavReorderableItemWrapper } from '~/components/drag-n-drop/LeftNavReorderableItemWrapper'
import { useReorderableItemMonitorContainer } from '~/components/drag-n-drop/useReorderableItemMonitorContainer'
import { SortRow } from './CustomSortPanelRow'
import { TableColumnOption } from './TableColumnAutocomplete'

interface SortPanelProps {
  columns: TableColumnOption[]
  sortModel: GridSortModel
  onSortModelChange: (model: GridSortModel) => void
  tableId: string
  readOnly: boolean
  pendingSortField: string | null
  pendingSortDirection: 'asc' | 'desc' | 'delete' | null
  /** When true, allow editing if the user can view the table (e.g. ad-hoc lookup refinement). */
  lookupRefinementMode?: boolean
}

interface SortItemWithOrder extends GridSortItem {
  id: string
  name: string
  order: number
  icon?: React.ReactNode
}

export function CustomSortPanel({
  columns,
  sortModel,
  onSortModelChange,
  tableId,
  readOnly,
  pendingSortField,
  pendingSortDirection,
  lookupRefinementMode = false,
}: SortPanelProps) {
  const { t } = useTypedTranslation()
  const { permissionsManager } = useGlobalContext()

  const canUpdateSorts =
    (lookupRefinementMode
      ? permissionsManager.canViewTable(tableId)
      : permissionsManager.canConfigureTableTabs(tableId)) && !readOnly

  // Internal draft state
  const [draftModel, setDraftModel] = useState<GridSortModel>(sortModel)
  const [hasPopulated, setHasPopulated] = useState(false)

  useEffect(() => {
    setDraftModel(sortModel)
  }, [sortModel])

  // Populate with pending sort when panel opens
  useEffect(() => {
    if (pendingSortField && pendingSortDirection && !hasPopulated) {
      // Check if this column is already in the sort model
      const existingIndex = draftModel.findIndex((item) => item.field === pendingSortField)

      let newModel: GridSortItem[]

      if (pendingSortDirection === 'delete') {
        // Remove existing sort
        newModel = draftModel.filter((_, idx) => idx !== existingIndex)
      } else if (existingIndex >= 0) {
        // Update existing sort
        newModel = [...draftModel]
        newModel[existingIndex] = {
          field: pendingSortField,
          sort: pendingSortDirection,
        }
      } else {
        // Add new sort
        newModel = [
          ...draftModel,
          {
            field: pendingSortField,
            sort: pendingSortDirection,
          },
        ]
      }

      setDraftModel(newModel)
      onSortModelChange(newModel)

      setHasPopulated(true)
    }
  }, [pendingSortField, pendingSortDirection, draftModel, hasPopulated, onSortModelChange])

  // Reset populate flag when panel closes
  useEffect(() => {
    if (!pendingSortField && !pendingSortDirection) {
      setHasPopulated(false)
    }
  }, [pendingSortField, pendingSortDirection])

  // Transform sort items to include order for drag-and-drop
  const orderedSortItems: SortItemWithOrder[] = draftModel.map((item, index) => {
    const column = columns.find((col) => col.id === item.field)
    return {
      ...item,
      id: `sort-${item.field}-${index}`,
      name: column?.name ?? item.field,
      order: index,
    }
  })

  const handleAddSort = useCallback(() => {
    const firstColumn = first(columns)
    if (!firstColumn) {
      return
    }

    const newSort: GridSortItem = {
      field: firstColumn.id,
      sort: 'asc',
    }

    const newModel = [...draftModel, newSort]
    setDraftModel(newModel)
    onSortModelChange(newModel)
  }, [columns, draftModel, onSortModelChange])

  const handleRemoveSort = useCallback(
    (index: number) => {
      const updatedItems = draftModel.filter((_, i) => i !== index)
      setDraftModel(updatedItems)
      onSortModelChange(updatedItems)
    },
    [draftModel, onSortModelChange],
  )

  const handleRemoveAll = useCallback(() => {
    const newModel: GridSortModel = []
    setDraftModel(newModel)
    onSortModelChange(newModel)
  }, [onSortModelChange])

  const handleSortChange = useCallback(
    (newModel: GridSortModel) => {
      setDraftModel(newModel)
      onSortModelChange(newModel)
    },
    [onSortModelChange],
  )

  const handleReorderSort = useCallback(
    (itemId: string, newOrder: number) => {
      const oldIndex = orderedSortItems.findIndex((item) => item.id === itemId)
      if (oldIndex === -1) {
        return
      }

      const newModel = [...draftModel]
      const [movedItem] = newModel.splice(oldIndex, 1)
      newModel.splice(newOrder, 0, movedItem)

      setDraftModel(newModel)
      onSortModelChange(newModel)
    },
    [draftModel, onSortModelChange, orderedSortItems],
  )

  const containerRef = useReorderableItemMonitorContainer({
    scrollableDataType: 'sort-item',
    lowestOrder: 0,
    highestOrder: orderedSortItems.length - 1,
    orderedItems: orderedSortItems,
    handleReorderItem: handleReorderSort,
  })

  return (
    <div
      ref={containerRef}
      className={cn('flex flex-col gap-6', !lookupRefinementMode && 'w-[600px]')}
    >
      <div className="p-5">
        <div className="pb-2 text-xs text-gray-8">
          {lookupRefinementMode ? t('sort.sort_records_by') : t('sort.sort_view_by')}
        </div>
        <div className="flex flex-col gap-4 pt-4">
          {draftModel.map((item, index) => {
            const sortItemWithOrder = orderedSortItems[index]
            return (
              <LeftNavReorderableItemWrapper
                key={sortItemWithOrder.id}
                canDragAndDrop={canUpdateSorts}
                draggableItemType="sort-item"
                item={sortItemWithOrder}
              >
                <SortRow
                  columns={columns}
                  disableFields={!canUpdateSorts}
                  index={index}
                  item={item}
                  onRemove={handleRemoveSort}
                  onSortModelChange={handleSortChange}
                  sortModel={draftModel}
                  tableId={tableId}
                />
              </LeftNavReorderableItemWrapper>
            )
          })}
        </div>
      </div>
      <div className="flex justify-between border-t border-gray-4 px-5 py-4">
        <Button
          className="flex items-center gap-1"
          disabled={!canUpdateSorts}
          onClick={handleAddSort}
          size="sm"
          variant="ghost"
        >
          <Plus className="size-4" />
          {t('sort.add_sort')}
        </Button>
        {draftModel.length > 0 && (
          <Button
            className="flex items-center gap-1"
            disabled={!canUpdateSorts}
            onClick={handleRemoveAll}
            size="sm"
            variant="ghost"
          >
            <Trash2 className="size-4" />
            {t('filter.remove_all')}
          </Button>
        )}
      </div>
    </div>
  )
}
